import { createClient as createSupabaseClient } from '@supabase/supabase-js';
import { getBrand } from '@/lib/branding';

/**
 * Echidna's own Supabase project (2026-07-31, user call: separate projects).
 *
 * Literals, not env vars, and that is deliberate — the same reasoning as the
 * Firebase config and the GA4 id (nkr1.33): a Supabase URL and publishable
 * key ship inside every copy of the client and are gated by RLS, not by
 * secrecy. Literals also work under plain `vite dev`, which never runs
 * build-web.cjs and would otherwise read the PokerBlitz env from .env.local
 * and silently point an Echidna dev session at the wrong database.
 *
 * The poker path below stays env-driven and byte-identical in behaviour —
 * including the v1.8.13 fail-loud guard, which exists because an Android
 * build once shipped with the fallback URL baked in.
 */
const ECHIDNA_SUPABASE = {
  url: 'https://tqzuvwjglikwybpkisdz.supabase.co',
  key: 'sb_publishable_rR5HWLR0XgQyquv9617d9Q_4Hi-gk4A',
} as const;

const isEchidna = (() => {
  try {
    return getBrand().key === 'echidna';
  } catch {
    return false;
  }
})();

const supabaseUrl = isEchidna ? ECHIDNA_SUPABASE.url : import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = isEchidna ? ECHIDNA_SUPABASE.key : import.meta.env.VITE_SUPABASE_ANON_KEY;

const url = supabaseUrl?.trim();
const key = supabaseAnonKey?.trim();

const isUsingTestValues = !url || !key ||
  url === 'https://test.supabase.co' ||
  key === 'test-anon-key' ||
  !url.startsWith('https://') ||
  !url.includes('.supabase.co');

// Production builds MUST have real Supabase env vars baked in by Vite. If
// they're missing here, the build environment (CI, fastlane, local) didn't
// export VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY before `vite build` ran,
// and the resulting bundle is permanently pinned to a non-existent project.
//
// v1.8.13 Android shipped this way: CI didn't pass the secrets, the AAB
// silently fell back to https://test.supabase.co, and every user who
// signed in was dropped onto a "test" account. Fail the bundle at module
// load instead so the next regression is impossible to miss.
//
// Escape hatch: VITE_ALLOW_TEST_SUPABASE_FALLBACK=true lets the smoke
// CI step build the bundle without secrets. Never set this in any
// deploy pipeline — the workflow `test -n` guards already enforce
// presence at deploy time, so this only matters for tests.
const allowTestFallback = import.meta.env.VITE_ALLOW_TEST_SUPABASE_FALLBACK === 'true';
if (isUsingTestValues && !import.meta.env.DEV && !allowTestFallback) {
  const msg =
    'Supabase environment variables are missing from this production build. ' +
    'Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in the build environment ' +
    '(CI secrets / .env.local) BEFORE running vite build — they are inlined at ' +
    'build time and cannot be fixed at runtime.';
  console.error('❌ ' + msg, {
    hasUrl: !!url,
    hasKey: !!key,
    urlValue: url ? `${url.substring(0, 30)}...` : 'missing',
  });
  throw new Error(msg);
}

if (isUsingTestValues && import.meta.env.DEV) {
  console.warn('⚠️ Supabase configuration warning:', {
    hasUrl: !!url,
    hasKey: !!key,
    urlValue: url ? `${url.substring(0, 30)}...` : 'missing',
    keyValue: key ? `${key.substring(0, 10)}...` : 'missing',
    message: 'Using test/mock values. Authentication may not work. Check your .env.local file.',
  });
}

const finalUrl = url || 'https://test.supabase.co';
const finalKey = key || 'test-anon-key';

export const supabase = createSupabaseClient(finalUrl, finalKey);

// Add a connection test helper
export async function testSupabaseConnection(): Promise<{ success: boolean; error?: string }> {
  try {
    const { data: _data, error } = await supabase.auth.getSession();
    if (error) {
      return { success: false, error: error.message };
    }
    // If we can get a session (even if null), connection works
    return { success: true };
  } catch (error) {
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown connection error' 
    };
  }
}

export function createClient() {
  return supabase;
}